import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import {
  grupoEmpresaLabel,
  PROCESOS_EMPRESA_PAGE_SIZE,
  procesosEmpresaQueryFilters,
} from './procesos-empresa-routes';

export interface ProcesosEmpresaRow {
  id: number;
  rfc?: string;
  razonSocial?: string;
  grupoEmpresarial?: {
    alias?: string;
    grpAliasEquilibrio?: string;
    grpAliasDecomsa?: string;
  } | null;
}

export interface ProcesosEmpresasViewProps {
  active?: boolean;
  onSelectEmpresa: (empresa: ProcesosEmpresaRow) => void;
}

export function ProcesosEmpresasView({
  active = true,
  onSelectEmpresa,
}: ProcesosEmpresasViewProps) {
  const [rows, setRows] = useState<ProcesosEmpresaRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PROCESOS_EMPRESA_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'razonSocial', sort: 'asc' },
  ]);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!active) return;
    setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [active, debouncedQuery]);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [active]);

  const fetchEmpresas = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const sortItem = sortModel[0] ?? { field: 'razonSocial', sort: 'asc' as const };
      const params = new URLSearchParams();
      params.set('page', String(paginationModel.page));
      params.set('size', String(PROCESOS_EMPRESA_PAGE_SIZE));
      params.set('sort', `${sortItem.field},${sortItem.sort}`);
      procesosEmpresaQueryFilters(debouncedQuery).forEach(f => params.append(f.filterName, f.filterValue));
      params.set('cacheBuster', String(Date.now()));
      const res = await axios.get<ProcesosEmpresaRow[]>(`api/empresas?${params.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotalItems(parseInt(String(res.headers['x-total-count'] ?? '0'), 10) || 0);
    } catch {
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [active, debouncedQuery, paginationModel.page, sortModel]);

  useEffect(() => {
    void fetchEmpresas();
  }, [fetchEmpresas]);

  const cols: GridColDef[] = useMemo(
    () => [
      {
        field: 'grupo',
        headerName: 'Grupo',
        flex: 0.8,
        minWidth: 120,
        sortable: false,
        renderCell: params => {
          const label = grupoEmpresaLabel((params.row as ProcesosEmpresaRow).grupoEmpresarial);
          return label || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
      {
        field: 'rfc',
        headerName: 'RFC',
        flex: 0.8,
        minWidth: 120,
        renderCell: params => {
          const row = params.row as ProcesosEmpresaRow;
          const label = row.rfc?.trim();
          if (!label) return <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start"
              style={{ fontSize: 13, textDecoration: 'none' }}
              onClick={() => onSelectEmpresa(row)}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: 'razonSocial',
        headerName: 'Razón social',
        flex: 1.6,
        minWidth: 180,
        renderCell: params => {
          const row = params.row as ProcesosEmpresaRow;
          return row.razonSocial?.trim() || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
    ],
    [onSelectEmpresa],
  );

  return (
    <>
      <p style={s.hint}>Seleccione una empresa para ver sus workflows y crear procesos.</p>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Buscar por grupo, alias, nombre o RFC…"
        style={s.input}
        aria-label="Buscar empresa por grupo, alias, nombre o RFC"
        autoComplete="off"
      />
      <DataGrid
        rows={rows}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        pagination
        paginationMode="server"
        sortingMode="server"
        disableRowSelectionOnClick
        disableColumnMenu
        paginationModel={paginationModel}
        onPaginationModelChange={model => {
          setPaginationModel({ ...model, pageSize: PROCESOS_EMPRESA_PAGE_SIZE });
        }}
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(model.length > 0 ? model : [{ field: 'razonSocial', sort: 'asc' }]);
        }}
        onRowClick={params => onSelectEmpresa(params.row as ProcesosEmpresaRow)}
        rowCount={totalItems}
        loading={loading}
        pageSizeOptions={[PROCESOS_EMPRESA_PAGE_SIZE]}
        localeText={{ noRowsLabel: query.trim() ? 'Sin empresas para esa búsqueda' : 'Sin empresas' }}
        sx={{
          ...profaseDataGridSx,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />
    </>
  );
}

interface ProcesosEmpresasModalProps {
  open: boolean;
  onClose: () => void;
  onSelectEmpresa: (empresa: ProcesosEmpresaRow) => void;
}

export default function ProcesosEmpresasModal({
  open,
  onClose,
  onSelectEmpresa,
}: ProcesosEmpresasModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-empresas-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-empresas-modal-title" className="profase-modal-title m-0">
          <FaBuilding style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Empresas
        </h2>
        <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
          Cerrar
        </button>
      </div>
      <ProcesosEmpresasView active={open} onSelectEmpresa={onSelectEmpresa} />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
    background: 'var(--crm-input-bg, #fff)',
    color: 'var(--crm-text-primary, #0f172a)',
    boxSizing: 'border-box',
    marginBottom: 12,
  },
};
